<template>
  <div>
    <div class="title-page flex items-center" style="padding: 1.3rem">
      <feather-icon icon="PackageIcon" svgClasses="h-4 w-4"></feather-icon>
      <h3 class="font-bold mr-3" style="display: inline-block; font-size: 1.125rem">
        {{ $t("internationalShipmentDetails") }}
      </h3>
    </div>
    <div class="line"></div>

    <div v-if="loading" class="p-6 loading-table">
      <spinner-color />
    </div>

    <div v-else-if="error_msg || !shipment_detail" style="padding: 1.3rem">
      <vs-alert color="danger" icon="new_releases" :active="true">
        <span>{{ error_msg || $t("sorryDataTable") }}</span>
      </vs-alert>
    </div>

    <div v-else style="padding: 1.3rem">
      <!-- Timeline -->
      <vs-card class="mb-4">
        <ul class="intl-timeline">
          <li
            v-for="(step, index) in shipment_detail.timeline"
            :key="index"
            :class="{ completed: step.is_completed, current: step.is_current }"
          >
            <span class="dot"></span>
            <span class="label">{{ step.label }}</span>
            <span class="timestamp" v-if="step.timestamp">{{ formatDate(step.timestamp) }}</span>
          </li>
        </ul>
      </vs-card>

      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-4">
          <vs-card>
            <h4 class="mb-3">{{ $t('shipmentData') }}</h4>
            <p class="mb-2">{{ $t("code") }} : <span class="font-semibold">{{ shipment_detail.code }}</span></p>
            <p class="mb-2">{{ $t("statusOfOrder") }} : <span class="font-semibold">{{ shipment_detail.status.label }}</span></p>
            <p class="mb-2">{{ $t("cartonsCount") }} : <span class="font-semibold">{{ shipment_detail.cartons_count }}</span></p>
            <p class="mb-2">{{ $t("totalWeightKg") }} : <span class="font-semibold">{{ shipment_detail.total_weight_kg }}</span></p>
            <template v-if="shipment_detail.actual_weight_kg">
              <p class="mb-2">{{ $t("actualCartonsCount") }} : <span class="font-semibold">{{ shipment_detail.actual_cartons_count }}</span></p>
              <p class="mb-2">{{ $t("actualWeightKg") }} : <span class="font-semibold">{{ shipment_detail.actual_weight_kg }}</span></p>
            </template>
            <p class="mb-2 break-text">{{ $t("productDescription") }} : <span class="font-semibold">{{ shipment_detail.product_description || '-' }}</span></p>
            <p v-if="shipment_detail.notes" class="break-text">{{ $t("notesOrder") }} : <span class="font-semibold">{{ shipment_detail.notes }}</span></p>
          </vs-card>
        </div>

        <div class="vx-col w-full md:w-1/2 mb-4">
          <vs-card>
            <h4 class="mb-3">{{ $t('costAndPayment') }}</h4>
            <div class="cost-payment-list">
              <div class="cost-payment-row">
                <span class="cost-payment-row__label">{{ $t("costPerKgUsd") }}</span>
                <span class="cost-payment-row__value">{{ shipment_detail.cost_per_kg_usd != null ? '$ ' + shipment_detail.cost_per_kg_usd : '-' }}</span>
              </div>
              <div class="cost-payment-row">
                <span class="cost-payment-row__label">{{ $t("totalCostUsd") }}</span>
                <span class="cost-payment-row__value">{{ shipment_detail.total_cost_usd != null ? '$ ' + shipment_detail.total_cost_usd : '-' }}</span>
              </div>
              <div class="cost-payment-row">
                <span class="cost-payment-row__label">{{ $t("paidAmountUsd") }}</span>
                <span class="cost-payment-row__value">$ {{ shipment_detail.paid_amount_usd }}</span>
              </div>
              <div class="cost-payment-row">
                <span class="cost-payment-row__label">{{ $t("remainingAmountUsd") }}</span>
                <span class="cost-payment-row__value cost-payment-row__value--remaining">{{ shipment_detail.remaining_amount_usd != null ? '$ ' + shipment_detail.remaining_amount_usd : '-' }}</span>
              </div>
            </div>
            <div class="cost-payment-status">
              <vs-chip :color="shipment_detail.is_fully_paid ? 'success' : 'warning'">
                {{ shipment_detail.is_fully_paid ? $t("fullyPaid") : $t("notFullyPaid") }}
              </vs-chip>
            </div>
          </vs-card>

          <vs-card class="mt-4" v-if="shipment_detail.warehouse">
            <h4 class="mb-3">{{ $t('warehouseAndTrip') }}</h4>
            <p class="mb-2">{{ $t("currentWarehouse") }} : <span class="font-semibold">{{ shipment_detail.warehouse.name }}</span></p>
            <p v-if="shipment_detail.trip">{{ $t("relatedTrip") }} : <span class="font-semibold">{{ shipment_detail.trip.code }} - {{ shipment_detail.trip.status.label }}</span></p>
          </vs-card>

          <vs-card class="mt-4" v-if="shipment_detail.local_order">
            <h4 class="mb-3">{{ $t('localOrder') }}</h4>
            <p>{{ $t("code") }} : <span class="font-semibold">{{ shipment_detail.local_order.code }}</span> - {{ shipment_detail.local_order.status }}</p>
          </vs-card>
        </div>
      </div>

      <!-- Images -->
      <vs-card class="mb-4" v-if="hasImages">
        <h4 class="mb-3">{{ $t('shipmentImages') }}</h4>
        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2">
            <h6 class="mb-2">{{ $t("productImages") }}</h6>
            <div class="flex flex-wrap">
              <a
                v-for="(img, index) in shipment_detail.images.product"
                :key="'p' + index"
                :href="img"
                target="_blank"
                class="ml-2 mb-2"
              >
                <img :src="img" style="width: 90px; height: 90px; object-fit: cover; border-radius: 6px" />
              </a>
            </div>
          </div>
          <div class="vx-col w-full md:w-1/2">
            <h6 class="mb-2">{{ $t("cartonImages") }}</h6>
            <div class="flex flex-wrap">
              <a
                v-for="(img, index) in shipment_detail.images.carton"
                :key="'c' + index"
                :href="img"
                target="_blank"
                class="ml-2 mb-2"
              >
                <img :src="img" style="width: 90px; height: 90px; object-fit: cover; border-radius: 6px" />
              </a>
            </div>
          </div>
        </div>
      </vs-card>

      <!-- Wallet movements for this shipment -->
      <vs-card v-if="shipment_detail.wallet_movements && shipment_detail.wallet_movements.length">
        <h4 class="mb-3">{{ $t('internationalWalletMovements') }}</h4>
        <vs-table :data="shipment_detail.wallet_movements" noDataText="">
          <template slot="thead">
            <vs-th>{{ $t("date") }}</vs-th>
            <vs-th>{{ $t("movementType") }}</vs-th>
            <vs-th>{{ $t("amountUsd") }}</vs-th>
            <vs-th>{{ $t("balanceAfterUsd") }}</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr :data="tr" :key="index" v-for="(tr, index) in data">
              <vs-td>{{ formatDate(tr.created_at) }}</vs-td>
              <vs-td>{{ tr.type.label }}</vs-td>
              <vs-td>{{ tr.amount_usd }}</vs-td>
              <vs-td>{{ tr.balance_after_usd }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vs-card>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SpinnerColor from "@/views/components/table-components/SpinnerColor2.vue";

export default {
  components: {
    SpinnerColor,
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapGetters("internationalShipping", ["shipment_detail", "error_msg"]),
    hasImages() {
      return (
        this.shipment_detail &&
        this.shipment_detail.images &&
        (this.shipment_detail.images.product.length || this.shipment_detail.images.carton.length)
      );
    },
  },
  methods: {
    ...mapActions("internationalShipping", ["fetchShipmentDetails"]),
    formatDate(value) {
      return new Date(value).toLocaleDateString("en-LY", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Africa/Tripoli",
      });
    },
  },
  async created() {
    this.loading = true;
    await this.fetchShipmentDetails(this.$route.params.id);
    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vuexy/_loadingTable.scss";

.break-text {
  overflow-wrap: break-word;
  word-break: break-word;
}

.cost-payment-list {
  display: flex;
  flex-direction: column;
}

.cost-payment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  &:last-child {
    border-bottom: none;
  }

  &__label {
    color: #747474;
    font-size: 0.85rem;
  }

  &__value {
    font-weight: 600;
    direction: ltr;
    unicode-bidi: plaintext;
  }

  &__value--remaining {
    color: #ff9f43;
  }
}

.cost-payment-status {
  // vuesax's .con-vs-chip is floated, which pulls it out of normal flow and
  // lets it overlap whatever renders after this card unless the float is
  // contained here.
  overflow: hidden;
  margin-top: 10px;
}

.intl-timeline {
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    flex: 1;
    min-width: 120px;
    text-align: center;
    position: relative;
    padding: 0 8px;
    opacity: 0.5;

    .dot {
      display: block;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #e0e0e0;
      margin: 0 auto 6px;
    }

    .label {
      display: block;
      font-weight: 600;
      font-size: 0.8rem;
    }

    .timestamp {
      display: block;
      font-size: 0.7rem;
      color: #888;
    }

    &.completed {
      opacity: 1;

      .dot {
        background: #31006f;
      }
    }

    &.current {
      opacity: 1;

      .dot {
        background: #28c76f;
      }

      .label {
        color: #28c76f;
      }
    }
  }
}

@media (max-width: 576px) {
  .intl-timeline li {
    flex: 1 1 33%;
    min-width: 0;
    margin-bottom: 12px;
  }

  .cost-payment-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>
